// frontend/src/components/ScheduleSetup.js

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { BookX, LucideSave, X } from 'lucide-react';
import axios from '../axiosConfig';
import moment from 'moment-timezone'; // For UTC handling
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import { formatDate, parseLocalDate } from '../utils/dateTimeFormatter';
import { useDateTimePreferences } from '../hooks/useDateTimePreferences';

function timesOverlap(startA, endA, startB, endB) {
  const timeFormat = 'HH:mm';
  const timeAStart = moment(startA, timeFormat);
  const timeAEnd = moment(endA, timeFormat);
  const timeBStart = moment(startB, timeFormat);
  const timeBEnd = moment(endB, timeFormat);

  // Adjust for overnight times
  if (timeAEnd.isBefore(timeAStart)) timeAEnd.add(1, 'day');
  if (timeBEnd.isBefore(timeBStart)) timeBEnd.add(1, 'day');

  // Adjust zero-duration events by adding a minimal duration
  const minimalDuration = moment.duration(1, 'second');
  if (timeAStart.isSame(timeAEnd)) timeAEnd.add(minimalDuration);
  if (timeBStart.isSame(timeBEnd)) timeBEnd.add(minimalDuration);

  // console.log('timeAStart:', timeAStart);
  // console.log('timeAEnd:', timeAEnd);
  // Overlap occurs if time ranges intersect
  return timeAStart.isBefore(timeBEnd) && timeAEnd.isAfter(timeBStart);
}

function validateTimeSlots(timeSlots) {
  for (let i = 0; i < timeSlots.length; i++) {
    const slotA = timeSlots[i];
    for (let j = i + 1; j < timeSlots.length; j++) {
      const slotB = timeSlots[j];

      if (timesOverlap(slotA.start_time, slotA.end_time, slotB.start_time, slotB.end_time)) {
        // Overlap detected
        return {
          isValid: false,
          conflicts: [
            {
              slotAIndex: i,
              slotBIndex: j,
            },
          ],
        };
      }
    }
  }
  return { isValid: true }; 
}



const ScheduleSetup = ({ 
  currentEntity, 
  onClose,
  onCancel, 
  isEditing, 
  isCopying,
  onScheduleChanged,
  initialSchedule = {} 
}) => {
  const { t } = useTranslation();
  const { background, text, border, cn } = useTheme();
  // Force re-render when preferences change
  useDateTimePreferences();
  
  // Track changes for each section
  const [sectionChanges, setSectionChanges] = useState({
    schedules: false,
    excludedPeriods: false,
    timeSlot: false,
    onCall: false
  });

  const hasChanges = isEditing ? 
    Object.values(sectionChanges).some(changed => changed) : 
    true;  // Always true for new schedules

  const userId = parseInt(localStorage.getItem('userId'), 10);

  // Determine if the schedule is being edited, copied, or created
  // const isCopying = initialSchedule && !isEditing;
  // Initialize state based on mode: create, copy, or edit
  // Helper function to ensure included_days is always an array
  // Helper function to convert string to array
  const stringToArray = (str) => {
    if (typeof str === 'string') {
      return str.split(',').map(Number).filter(day => !isNaN(day) && day >= 0 && day <= 6);
    }
    return Array.isArray(str) ? str : [0, 1, 2, 3, 4, 5, 6];
  };

  // Helper function to convert array to string
  const arrayToString = (arr) => {
    return Array.isArray(arr) ? arr.join(',') : '';
  };


  // Initialize state while preserving the original excluded_periods data

  const toLocalDate = (value) => {
    const parsed = parseLocalDate(value);
    return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : null;
  };

  const [schedule, setSchedule] = useState(() => {
    return {
      ...initialSchedule,
      name: isEditing ? initialSchedule.name : isCopying ? `${initialSchedule.name} (Copy)` : '',
      type: isEditing || isCopying ? initialSchedule.type : 'fixed',
      start_date: initialSchedule.start_date ? toLocalDate(initialSchedule.start_date) : "",
      end_date: initialSchedule.end_date ? toLocalDate(initialSchedule.end_date) : "",
      included_days: stringToArray(initialSchedule.included_days),
      time_slots: Array.isArray(initialSchedule.time_slots)
        ? initialSchedule.time_slots.map(slot => ({
            type: slot.type || 'single',
            start_time: slot.start_time || '08:00',
            end_time: slot.end_time || '08:00',
            frequency: slot.frequency || 60,
          }))
        : [{ type: 'single', start_time: '08:00', end_time: '08:00' }],
      on_call_start_time: initialSchedule.on_call_start_time || '23:00',
      on_call_end_time: initialSchedule.on_call_end_time || '05:00',
      on_call_min_gap: initialSchedule.on_call_min_gap || 30,
      is_common: initialSchedule.is_common || false,
      is_predefined: initialSchedule.is_predefined || false,
      excluded_periods: initialSchedule.excluded_periods?.map(period => ({
        ...period,
        start_date: toLocalDate(period.start_date),
        end_date: toLocalDate(period.end_date)
      })) || [],
    };
  });
  
  useEffect(() => {
    if (isEditing) {
      setSchedule(prevSchedule => ({
        ...prevSchedule,
        ...initialSchedule,
        start_date: initialSchedule.start_date ? toLocalDate(initialSchedule.start_date) : "",
        end_date: initialSchedule.end_date ? toLocalDate(initialSchedule.end_date) : "",
        excluded_periods: initialSchedule.excluded_periods?.map(period => ({
          ...period,
          start_date: toLocalDate(period.start_date),
          end_date: toLocalDate(period.end_date),
        })) || [],
      }));
    }
  }, [isEditing, initialSchedule]);

  const [excludedPeriodStart, setExcludedPeriodStart] = useState(null);
  const [excludedPeriodEnd, setExcludedPeriodEnd] = useState(null);
  const [excludedPeriodLabel, setExcludedPeriodLabel] = useState('');

  // Compare function to detect changes in schedule fields
  const hasScheduleFieldsChanged = (newSchedule, originalSchedule) => {
    const scheduleFields = ['name', 'type', 'start_date', 'end_date', 'included_days', 'is_common', 'is_predefined'];
    return scheduleFields.some(field => {
      if (field === 'included_days') {
        const newIncludedDays = arrayToString(newSchedule[field]);
        const originalIncludedDays = originalSchedule[field];
        return newIncludedDays !== originalIncludedDays;
      }
      return JSON.stringify(newSchedule[field]) !== JSON.stringify(originalSchedule[field]);
    });
  };

  // Compare function for excluded periods
  const hasExcludedPeriodsChanged = (newPeriods, originalPeriods) => {
    if (!originalPeriods) return newPeriods.length > 0;
    if (newPeriods.length !== originalPeriods.length) return true;
    return JSON.stringify(newPeriods) !== JSON.stringify(originalPeriods);
  };

  // Compare function for time slots
  const hasTimeSlotsChanged = (newSlots, originalSlots) => {
    if (!originalSlots) return newSlots.length > 0;
    if (newSlots.length !== originalSlots.length) return true;
    return JSON.stringify(newSlots) !== JSON.stringify(originalSlots);
  };

  // Compare function for on-call details
  const hasOnCallDetailsChanged = (newSchedule, originalSchedule) => {
    const onCallFields = ['on_call_start_time', 'on_call_end_time', 'on_call_min_gap'];
    return onCallFields.some(field => 
      newSchedule[field] !== originalSchedule[field]
    );
  };

  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setSchedule(prev => {
      const newSchedule = { ...prev, [field]: value };
      
      if (isEditing) {
        // Determine which section changed and update tracking
        const scheduleBasicFields = ['name', 'type', 'start_date', 'end_date', 'included_days', 'is_common', 'is_predefined'];
        const onCallFields = ['on_call_start_time', 'on_call_end_time', 'on_call_min_gap'];
        
        if (scheduleBasicFields.includes(field)) {
          setSectionChanges(prev => ({ ...prev, schedules: hasScheduleFieldsChanged(newSchedule, initialSchedule) }));
        } else if (onCallFields.includes(field)) {
          setSectionChanges(prev => ({ ...prev, onCall: hasOnCallDetailsChanged(newSchedule, initialSchedule) }));
        }
      }
      
      return newSchedule;
    });
  };

 

  if (!currentEntity || !currentEntity.entityId) {
    console.error('currentEntity or entityId is missing');
    toast.error(t('Error: Entity information is missing. Please try again.'));
    return null;
  }

  const inputClassName = cn(
    "border rounded-lg py-2 px-3 text-sm transition-colors",
    "border-gray-300 dark:border-gray-600",
    "bg-white dark:bg-gray-700",
    "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
    "hover:border-gray-400 dark:hover:border-gray-500",
    text.primary
  );

  const daysOfWeek = [
    { id: 0, name: 'Sunday' },
    { id: 1, name: 'Monday' },
    { id: 2, name: 'Tuesday' },
    { id: 3, name: 'Wednesday' },
    { id: 4, name: 'Thursday' },
    { id: 5, name: 'Friday' },
    { id: 6, name: 'Saturday' }
  ];



  const handleDayToggle = (dayId) => {
    setSchedule(prevSchedule => {
      const currentIncludedDays = stringToArray(prevSchedule.included_days);
      const newIncludedDays = currentIncludedDays.includes(dayId)
        ? currentIncludedDays.filter(id => id !== dayId)
        : [...currentIncludedDays, dayId].sort((a, b) => a - b);
      
      const newSchedule = { 
        ...prevSchedule, 
        included_days: newIncludedDays 
      };
      
      if (isEditing) {
        setSectionChanges(prev => ({ 
          ...prev, 
          schedules: hasScheduleFieldsChanged(newSchedule, initialSchedule)
        }));
      }
      
      return newSchedule;
    });
  };

  const handleTimeSlotChange = (index, field, value) => {
    setSchedule(prev => {
      const newTimeSlots = [...(prev.time_slots || [])];
      if (index >= 0 && index < newTimeSlots.length) {
        if (field === 'type') {
          newTimeSlots[index] = value === 'single'
            ? { type: 'single', start_time: newTimeSlots[index].start_time || '08:00', end_time: newTimeSlots[index].start_time || '08:00' }
            : { type: 'recurring', start_time: newTimeSlots[index].start_time || '08:00', end_time: newTimeSlots[index].end_time || '23:59', frequency: newTimeSlots[index].frequency || 60 };
        } else {
          newTimeSlots[index] = { ...newTimeSlots[index], [field]: value };
          if (newTimeSlots[index].type === 'single' && field === 'start_time') {
            newTimeSlots[index].end_time = value;
          }
        }
      }
      
      if (isEditing) {
        setSectionChanges(prev => ({ 
          ...prev, 
          timeSlot: hasTimeSlotsChanged(newTimeSlots, initialSchedule.time_slots)
        }));
      }
      
      return { ...prev, time_slots: newTimeSlots };
    });
  };


  const addTimeSlot = () => {
    setSchedule(prevSchedule => {
      const newTimeSlots = [
        ...(prevSchedule.time_slots || []), 
        { type: 'single', start_time: '08:00', end_time: '08:00' }
      ];
      
      if (isEditing) {
        setSectionChanges(prev => ({ 
          ...prev, 
          timeSlot: hasTimeSlotsChanged(newTimeSlots, initialSchedule.time_slots)
        }));
      }
      
      return {
        ...prevSchedule,
        time_slots: newTimeSlots
      };
    });
  };
  
  const removeTimeSlot = (index) => {
    setSchedule(prevSchedule => {
      const newTimeSlots = prevSchedule.time_slots.filter((_, i) => i !== index);
      
      if (isEditing) {
        setSectionChanges(prev => ({ 
          ...prev, 
          timeSlot: hasTimeSlotsChanged(newTimeSlots, initialSchedule.time_slots)
        }));
      }
      
      return {
        ...prevSchedule,
        time_slots: newTimeSlots
      };
    });
  };


  const addExcludedPeriod = () => {
    if (excludedPeriodStart && excludedPeriodEnd && excludedPeriodLabel) {
      const newPeriod = {
        start_date: excludedPeriodStart,
        end_date: excludedPeriodEnd,
        label: excludedPeriodLabel
      };

      setSchedule(prev => {
        const newExcludedPeriods = [...prev.excluded_periods, newPeriod];
        
        if (isEditing) {
          setSectionChanges(prev => ({ 
            ...prev, 
            excludedPeriods: hasExcludedPeriodsChanged(newExcludedPeriods, initialSchedule.excluded_periods)
          }));
        }
        
        return {
          ...prev,
          excluded_periods: newExcludedPeriods
        };
      });
      
      setExcludedPeriodStart(null);
      setExcludedPeriodEnd(null);
      setExcludedPeriodLabel('');
    }
  };

  const removeExcludedPeriod = (index) => {
    setSchedule(prevSchedule => {
      const newExcludedPeriods = prevSchedule.excluded_periods.filter((_, i) => i !== index);
      
      if (isEditing) {
        setSectionChanges(prev => ({ 
          ...prev, 
          excludedPeriods: hasExcludedPeriodsChanged(newExcludedPeriods, initialSchedule.excluded_periods)
        }));
      }
      
      return {
        ...prevSchedule,
        excluded_periods: newExcludedPeriods
      };
    });
  };

  // Frontend validation function
  const validateScheduleData = (schedule) => {
    const errors = [];

    // Validate name
    if (!schedule.name?.trim()) {
      errors.push(t('Schedule name is required'));
    }

    // Validate dates
    if (!schedule.start_date) {
      errors.push(t('Start date is required'));
    }
    
    if (!schedule.end_date) {
      errors.push(t('End date is required'));
    }

    // Validate included days
    if (!schedule.included_days || 
        (Array.isArray(schedule.included_days) && schedule.included_days.length === 0) ||
        (typeof schedule.included_days === 'string' && !schedule.included_days.trim())) {
      errors.push(t('At least one day of the week must be selected'));
    }

    // If dates exist, validate end date is after start date
    if (schedule.start_date && schedule.end_date) {
      const startDate = moment(schedule.start_date);
      const endDate = moment(schedule.end_date);
      if (endDate.isBefore(startDate)) {
        errors.push(t('End date must be after start date'));
      }
    }

    // For fixed schedule type, validate time slots
    if (schedule.type === 'fixed') {
      if (!Array.isArray(schedule.time_slots) || schedule.time_slots.length === 0) {
        errors.push(t('At least one time slot is required for fixed schedules'));
      }
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate all required fields
    const validationErrors = validateScheduleData(schedule);
    if (validationErrors.length > 0) {
      validationErrors.forEach(error => toast.error(error));
      setIsLoading(false);
      return;
    }

    console.log('initialSchedule.included_days:', initialSchedule.included_days);
    const scheduleData = {
      // Basic schedule data
      
      schedules: {
        name: schedule.name,
        type: schedule.type,
        start_date: schedule.start_date,
        end_date: schedule.end_date,
        // Only update included_days if it's actually changed or if creating new schedule
        included_days: isEditing ? 
          (arrayToString(schedule.included_days) || initialSchedule.included_days) : 
          arrayToString(schedule.included_days),
        is_common: schedule.is_common,
        is_predefined: schedule.is_predefined,
        entity_id: schedule.is_common ? null : currentEntity.entityId,
        created_by: userId,
      },
      // Change flags
      schedules_change: isEditing ? (sectionChanges.schedules ? 1 : 0) : 1,
      excludedPeriods_change: isEditing ? (sectionChanges.excludedPeriods ? 1 : 0) : 1,
      time_slot_change: isEditing ? (sectionChanges.timeSlot ? 1 : 0) : 1,
      on_call_change: isEditing ? (sectionChanges.onCall ? 1 : 0) : 1,
    };

    // Add excluded periods if present
    if (schedule.excluded_periods?.length > 0) {
      scheduleData.excluded_periods = schedule.excluded_periods.map(period => ({
        ...period,
        start_date: period.start_date,
        end_date: period.end_date,
        label: period.label,
        // Include id if it exists (for existing periods)
        ...(period.id && { id: period.id })
      }));
    }

    // Add appropriate schedule details based on type
    if (schedule.type === 'on_call') {
      scheduleData.on_call_details = {
        on_call_start_time: schedule.on_call_start_time,
        on_call_end_time: schedule.on_call_end_time,
        on_call_min_gap: schedule.on_call_min_gap
      };
    } else {
      scheduleData.time_slots = schedule.time_slots.map(slot => ({
        type: slot.type,
        start_time: slot.start_time,
        end_time: slot.type === 'recurring' ? slot.end_time : slot.start_time,
        frequency: slot.type === 'recurring' ? parseInt(slot.frequency) : null,
      }));

      // Validate time slots
      const validationResult = validateTimeSlots(scheduleData.time_slots);
      if (!validationResult.isValid) {
        const conflicts = validationResult.conflicts.map(conflict => {
          const slotAIndex = conflict.slotAIndex + 1;
          const slotBIndex = conflict.slotBIndex + 1;
          return t(`Time slot ${slotAIndex} overlaps with time slot ${slotBIndex}`);
        }).join('\n');
        
        toast.error(t(`The following time slots overlap:\n${conflicts}`));
        setIsLoading(false);
        return;
      }
    }
    console.log('scheduleData:', scheduleData);
    try {
      let response;  // Declare response variable outside if/else

      if (isEditing) {
        response = await axios.put(`/api/schedules/${currentEntity.entityId}/${initialSchedule.id}`, scheduleData);
        setSectionChanges(false);
        toast.success(t(`Schedule ${schedule.name} updated successfully!`));
      } else {
        response = await axios.post(`/api/schedules/${currentEntity.entityId}`, scheduleData);
        toast.success(t(`Schedule: ${schedule.name} created successfully!`));
      }
  
      if (onScheduleChanged) {
        onScheduleChanged(response.data);
      }
      
      onClose(); // Close modal after successful operation
    } catch (error) {
      console.error('Error saving schedule', error);
      toast.error(t('Failed to save schedule. Please try again.'));
    } finally {
      setIsLoading(false);
    }
  };
  
  //when cancel is clicked
  const handleCancelClick = () => {
    onCancel(true); // Notify the parent that cancel was clicked
  };

  return (
    <div className={cn("flex flex-col h-full", background.primary)}>
      {/* Header */}
      <div className={cn(
        "flex items-center justify-between p-6",
        border.default,
        "border-b"
      )}>
        <h2 className={cn("text-xl font-bold", text.primary)}>
          {isEditing ? t('Edit Schedule') : t('Create New Schedule')}
        </h2>
        <button
          onClick={onClose}
          className={cn(
            "p-2 rounded-lg transition-colors",
            "hover:bg-gray-100 dark:hover:bg-gray-700",
            text.secondary
          )}
        >
          <X size={20} />
        </button>
      </div>

      <div className="flex-grow overflow-y-auto p-6" style={{ maxHeight: 'calc(100vh - 200px)' }}>
        <form onSubmit={handleSubmit} className="space-y-6">

          <div className="max-w-[calc(1.5*176px)]"> {/* Width of  date picker inputs + gaps */}
            <label htmlFor="scheduleType" className={cn("block text-sm font-medium", text.primary)}>
              {t('Schedule Type')}
            </label>
            <select
              id="scheduleType"
              value={schedule.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              className={`${inputClassName} w-full`}
            >
              <option value="fixed">{t('Fixed Schedule')}</option>
              <option value="on_call">{t('On-Call Schedule')}</option>
            </select>
          </div>

          <div className="max-w-[calc(4*176px+3*0.5rem)]"> {/* Width of 4 date picker inputs + gaps */}
            <label htmlFor="scheduleName" className="block text-sm font-medium text-gray-700">
              {t('Schedule Name')}
            </label>
            <input
              type="text"
              id="scheduleName"
              value={schedule.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={`${inputClassName} w-full`}
              required
              // readOnly={isEditing} // Prevent renaming when editing
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Schedule Period')}
            </label>
            <div className="flex space-x-2 mb-2">
              <DatePicker
                selected={schedule.start_date}
                onChange={(date) => {
                  setSchedule(prev => {
                    const newSchedule = {
                      ...prev,
                      start_date: date
                    };
                    
                    if (isEditing) {
                      setSectionChanges(prev => ({ 
                        ...prev, 
                        schedules: hasScheduleFieldsChanged(newSchedule, initialSchedule)
                      }));
                    }
                    
                    return newSchedule;
                  });
                }}
                selectsStart
                startDate={schedule.start_date}
                endDate={schedule.end_date}
                placeholderText={t('Start Date')}
                className={inputClassName}
                dateFormat="yyyy-MM-dd"
                required
              />
              <DatePicker
                selected={schedule.end_date}
                onChange={(date) => {
                  setSchedule(prev => {
                    const newSchedule = {
                      ...prev,
                      end_date: date
                    };
                    
                    if (isEditing) {
                      setSectionChanges(prev => ({ 
                        ...prev, 
                        schedules: hasScheduleFieldsChanged(newSchedule, initialSchedule)
                      }));
                    }
                    
                    return newSchedule;
                  });
                }}
                selectsEnd
                startDate={schedule.start_date}
                endDate={schedule.end_date}
                minDate={schedule.start_date}
                placeholderText={t('End Date')}
                className={inputClassName}
                dateFormat="yyyy-MM-dd"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Included Days of Week')}
            </label>
            <div className="flex flex-wrap gap-2">
              {daysOfWeek.map((day) => (
                <label key={day.id} className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={stringToArray(schedule.included_days).includes(day.id)}
                    onChange={() => handleDayToggle(day.id)}
                    className={`${inputClassName} h-4 w-4 text-indigo-600`}
                  />
                  <span className="ml-2 text-gray-700">{t(day.name)}</span>
                </label>
              ))}
            </div>
          </div>

          {schedule.type !== 'on_call' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('Time Slots')}
              </label>
              {schedule.time_slots.map((slot, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <select
                    value={slot.type}
                    onChange={(e) => handleTimeSlotChange(index, 'type', e.target.value)}
                    className={inputClassName}
                  >
                    <option value="single">{t('Single Trip')}</option>
                    <option value="recurring">{t('Recurring Trips')}</option>
                  </select>
                  
                  <input
                    type="time"
                    value={slot.start_time}
                    onChange={(e) => handleTimeSlotChange(index, 'start_time', e.target.value)}
                    className={inputClassName}
                    required
                  />
                  
                  {slot.type === 'recurring' && (
                    <>
                      <input
                        type="time"
                        value={slot.end_time}
                        onChange={(e) => handleTimeSlotChange(index, 'end_time', e.target.value)}
                        className={inputClassName}
                        required
                      />
                      <input
                        type="number"
                        value={slot.frequency}
                        onChange={(e) => handleTimeSlotChange(index, 'frequency', e.target.value)}
                        placeholder={t('Frequency (minutes)')}
                        className={inputClassName}
                        required
                      />
                    </>
                  )}
                  
                  <button type="button" onClick={() => removeTimeSlot(index)} className="text-red-600 hover:text-red-800">
                    {t('Remove')}
                  </button>
                </div>
              ))}
              <button type="button" onClick={addTimeSlot} className="text-indigo-600 hover:text-indigo-800">
                {t('Add Time Slot')}
              </button>
            </div>
          )}

          {schedule.type === 'on_call' && (
            <div className="space-y-4">
              <div>
                <label htmlFor="onCallStartTime" className="block text-sm font-medium text-gray-700">
                  {t('On-Call Start Time')}
                </label>
                <input
                  type="time"
                  id="onCallStartTime"
                  value={schedule.on_call_start_time}
                  onChange={(e) => handleInputChange('on_call_start_time', e.target.value)}
                  className={inputClassName}
                  required
                />
              </div>
              <div>
                <label htmlFor="onCallEndTime" className="block text-sm font-medium text-gray-700">
                  {t('On-Call End Time')}
                </label>
                <input
                  type="time"
                  id="onCallEndTime"
                  value={schedule.on_call_end_time}
                  onChange={(e) => handleInputChange('on_call_end_time', e.target.value)}
                  className={inputClassName}
                  required
                />
              </div>
              <div>
                <label htmlFor="onCallMinGap" className="block text-sm font-medium text-gray-700">
                  {t('Minimum Gap Between Trips (minutes)')}
                </label>
                <input
                  type="number"
                  id="onCallMinGap"
                  value={schedule.on_call_min_gap}
                  onChange={(e) => handleInputChange('on_call_min_gap', parseInt(e.target.value))}
                  min="1"
                  className={inputClassName}
                  required
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('Excluded Periods')}
            </label>
            <div className="flex space-x-2 mb-2">
              <DatePicker
                selected={excludedPeriodStart}
                onChange={(date) => setExcludedPeriodStart(date)}
                selectsStart
                startDate={excludedPeriodStart}
                endDate={excludedPeriodEnd}
                maxDate={excludedPeriodEnd}
                placeholderText={t('Start Date')}
                className={inputClassName}
                dateFormat="yyyy-MM-dd"
                required
              />
              <DatePicker
                selected={excludedPeriodEnd}
                onChange={(date) => setExcludedPeriodEnd(date)}
                selectsEnd
                startDate={excludedPeriodStart}
                endDate={excludedPeriodEnd}
                minDate={excludedPeriodStart}
                placeholderText={t('End Date')}
                className={inputClassName}
                dateFormat="yyyy-MM-dd"
                required
              />
              <input
                type="text"
                value={excludedPeriodLabel}
                onChange={(e) => setExcludedPeriodLabel(e.target.value)}
                placeholder={t('Label (e.g., Holiday)')}
                className={inputClassName}
                required
              />
              <button 
                type="button" 
                onClick={addExcludedPeriod}   
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                {t('Add')}
              </button>
            </div>
            
            <div className="max-w-[calc(4*176px+3*0.5rem)]"> {/* Match width with inputs above */}
              {schedule.excluded_periods.map((period, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-100 p-2 rounded-md mb-2">
                  <span>
                    {formatDate(period.start_date)} - {formatDate(period.end_date)} ({period.label})
                  </span>
                  <button type="button" onClick={() => removeExcludedPeriod(index)} className="text-red-600 hover:text-red-800">
                    {t('Remove')}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {schedule.type === 'on_call' && (
            <div>
              <p className="text-sm text-gray-600">
                {t('This on-call schedule will operate from')} {schedule.on_call_start_time} {t('to')} {schedule.on_call_end_time} {t('daily. Once a trip is requested, the next available trip will be scheduled no earlier than')} {schedule.on_call_min_gap} {t('minutes after the start of the previous trip.')}
              </p>
            </div>
          )}  

          <div className="flex items-center">
            <input
              id="isCommon"
              type="checkbox"
              checked={schedule.is_common}
              onChange={(e) => handleInputChange('is_common', e.target.checked)}
              className={`${inputClassName} h-4 w-4 text-indigo-600`}
            />
            <label htmlFor="isCommon" className="ml-2 block text-sm text-gray-900">
              {t('Common Schedule (available for all entities)')}
            </label>
          </div>

          <div className="flex items-center">
            <input
              id="isPredefined"
              type="checkbox"
              checked={schedule.is_predefined}
              onChange={(e) => handleInputChange('is_predefined', e.target.checked)}
              className={`${inputClassName} h-4 w-4 text-indigo-600`}
            />
            <label htmlFor="isPredefined" className="ml-2 block text-sm text-gray-900">
              {t('Predefined Schedule')}
            </label>
          </div>

        </form>
      </div>
      <div className={cn("sticky bottom-0 mt-6", background.primary)}>
        {/* Footer Actions */}
        <div className={cn(
          "flex justify-end items-center gap-3 p-6",
          border.default,
          "border-t"
        )}>
          <button
            type="button"
            onClick={handleSubmit}
            className={cn(
              "flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors shadow-sm",
              isEditing && !hasChanges 
                ? "bg-gray-400 cursor-not-allowed text-gray-700" 
                : "bg-blue-600 hover:bg-blue-700 text-white"
            )}
            disabled={isLoading || (isEditing && !hasChanges)}
          >
            <LucideSave size={16} />
            {isLoading 
              ? t(isEditing ? 'Updating...' : 'Creating...') 
              : t(isEditing ? 'Update Schedule' : 'Create Schedule')}
          </button>
          <button
            type="button"
            onClick={handleCancelClick}
            className={cn(
              "flex items-center gap-2 px-6 py-2 rounded-lg font-medium transition-colors shadow-sm",
              "bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600",
              "text-gray-800 dark:text-gray-200"
            )}
          >
            <BookX size={16} />
            {t('Cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleSetup;
